import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { useAuth } from '../contexts/AuthContext';

/**
 * Re-engagement questionnaire.  Shown when a disengaged user returns.
 *
 * Nine questions per programme spec.  Q9 is not a radio button — it's
 * a real user action (Reset Sobriety Clock vs Keep Current Clock) with
 * a mandatory confirmation dialog before a reset is finalised.  The
 * previous clock is archived server-side into `sobriety_clock_history`
 * so no journey data is ever lost.
 */

const Q1_OPTIONS = [
  { value: 'relapsed', label: 'Relapsed' },
  { value: 'life_circumstances', label: 'Life circumstances got in the way' },
  { value: 'overwhelmed_by_programme', label: 'Felt overwhelmed by the programme' },
  { value: 'lost_motivation', label: 'Lost motivation' },
  { value: 'technical_or_access', label: 'Technical or access problems' },
  { value: 'other', label: 'Other' },
];
const Q2_OPTIONS = [
  { value: 'less_than_a_week', label: 'Less than a week' },
  { value: 'one_to_four_weeks', label: '1 to 4 weeks' },
  { value: 'one_to_three_months', label: '1 to 3 months' },
  { value: 'three_to_six_months', label: '3 to 6 months' },
  { value: 'six_months_or_more', label: '6 months or more' },
  { value: 'not_applicable', label: 'Not applicable: no relapse' },
];
const Q4_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'some_but_not_enough', label: 'Some but not enough' },
];
const Q5_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'unsure', label: 'Unsure' },
  { value: 'not_really_but_trying', label: "Not really but I'm trying" },
];
const Q7_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Unsure' },
  { value: 'no_nominated_support', label: "I don't have a nominated support person" },
];

const Radio = ({ name, options, value, onChange, testidPrefix }) => (
  <div className="space-y-2">
    {options.map((o) => (
      <label
        key={o.value}
        className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
          value === o.value ? 'bg-blue-50 border-blue-400' : 'bg-white border-zinc-200 hover:border-zinc-300'
        }`}
      >
        <input
          type="radio"
          name={name}
          value={o.value}
          checked={value === o.value}
          onChange={() => onChange(o.value)}
          className="text-blue-600"
          data-testid={`${testidPrefix}-${o.value}`}
        />
        <span className="text-sm text-zinc-800">{o.label}</span>
      </label>
    ))}
  </div>
);

const QBlock = ({ n, title, children }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-3">
    <h3 className="text-sm font-bold text-zinc-900 mb-3">
      <span className="text-blue-600 mr-1.5">{n}.</span> {title}
    </h3>
    {children}
  </div>
);

export default function ReEngagementPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  const [answers, setAnswers] = useState({
    q1_reason: '',
    q2_recovery_length: '',
    q3_trigger: '',
    q4_had_support: '',
    q5_ready_to_reengage: '',
    q6_programme_barrier: '',
    q7_nok_would_help: '',
    q8_what_would_help: '',
    q9_clock_action: '',
  });

  const [newSobrietyDate, setNewSobrietyDate] = useState('');
  const [newSobrietyTime, setNewSobrietyTime] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (v) => setAnswers((a) => ({ ...a, [k]: v }));
  const setText = (k) => (e) => setAnswers((a) => ({ ...a, [k]: e.target.value }));

  const requiredAnswered =
    answers.q1_reason &&
    answers.q2_recovery_length &&
    answers.q4_had_support &&
    answers.q5_ready_to_reengage &&
    answers.q7_nok_would_help &&
    answers.q9_clock_action;

  const canSubmit =
    requiredAnswered &&
    (answers.q9_clock_action !== 'reset' || (newSobrietyDate && confirmReset));

  const doSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const body = {
        ...answers,
        q3_trigger: answers.q3_trigger || null,
        q6_programme_barrier: answers.q6_programme_barrier || null,
        q8_what_would_help: answers.q8_what_would_help || null,
      };
      if (answers.q9_clock_action === 'reset') {
        body.new_sobriety_date = newSobrietyDate;
        body.new_sobriety_time = newSobrietyTime || null;
      }
      const res = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reengagement/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      toast.success(
        answers.q9_clock_action === 'reset'
          ? 'Welcome back. Your previous clock is saved; your new clock has started.'
          : 'Welcome back. Your clock keeps running.'
      );
      if (typeof checkAuth === 'function') { try { await checkAuth(); } catch { /* non-fatal */ } }
      navigate('/relapse-message');
    } catch (e) {
      toast.error(e.message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="reengagement-page">
      <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-1 hover:bg-zinc-100 rounded"
          data-testid="reengagement-back"
        >
          <ArrowLeft className="w-5 h-5 text-zinc-700" />
        </button>
        <div className="flex-1">
          <h1 className="font-bold text-zinc-900 text-base">Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
          <p className="text-[11px] text-zinc-500">A short check-in before we resume your programme.</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <QBlock n="1" title="What best describes the reason you stepped away from the programme?">
          <Radio name="q1" options={Q1_OPTIONS} value={answers.q1_reason} onChange={set('q1_reason')} testidPrefix="q1" />
        </QBlock>

        <QBlock n="2" title="If a relapse was part of what happened, how long had you maintained your Recovery before this occurred?">
          <Radio name="q2" options={Q2_OPTIONS} value={answers.q2_recovery_length} onChange={set('q2_recovery_length')} testidPrefix="q2" />
        </QBlock>

        <QBlock n="3" title="Was there a specific event or trigger connected to stepping away? (Optional)">
          <textarea
            value={answers.q3_trigger}
            onChange={setText('q3_trigger')}
            className="w-full border border-zinc-300 rounded-lg p-2 text-sm min-h-[70px]"
            placeholder="Optional: free text"
            data-testid="q3-textarea"
            maxLength={1000}
          />
        </QBlock>

        <QBlock n="4" title="During this time, did you have support from family, friends or a Recovery community?">
          <Radio name="q4" options={Q4_OPTIONS} value={answers.q4_had_support} onChange={set('q4_had_support')} testidPrefix="q4" />
        </QBlock>

        <QBlock n="5" title="Do you feel ready to re-engage with the programme now?">
          <Radio name="q5" options={Q5_OPTIONS} value={answers.q5_ready_to_reengage} onChange={set('q5_ready_to_reengage')} testidPrefix="q5" />
        </QBlock>

        <QBlock n="6" title="Was there anything about the programme itself that made it harder to continue? (Optional)">
          <textarea
            value={answers.q6_programme_barrier}
            onChange={setText('q6_programme_barrier')}
            className="w-full border border-zinc-300 rounded-lg p-2 text-sm min-h-[70px]"
            placeholder="Optional: free text"
            data-testid="q6-textarea"
            maxLength={1000}
          />
        </QBlock>

        <QBlock n="7" title="Would having your nominated support person more involved have helped you stay engaged?">
          <Radio name="q7" options={Q7_OPTIONS} value={answers.q7_nok_would_help} onChange={set('q7_nok_would_help')} testidPrefix="q7" />
        </QBlock>

        <QBlock n="8" title="What would help you stay engaged this time? (Optional)">
          <textarea
            value={answers.q8_what_would_help}
            onChange={setText('q8_what_would_help')}
            className="w-full border border-zinc-300 rounded-lg p-2 text-sm min-h-[70px]"
            placeholder="Optional: free text"
            data-testid="q8-textarea"
            maxLength={1000}
          />
        </QBlock>

        {/* Q9: clock action.  Not a radio: two real buttons with an
            explicit confirmation step for the destructive-feeling one. */}
        <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-3" data-testid="q9-block">
          <h3 className="text-sm font-bold text-zinc-900 mb-1">
            <span className="text-blue-600 mr-1.5">9.</span> Do you want to reset your sobriety clock or keep your previous one intact?
          </h3>
          <p className="text-xs text-zinc-500 mb-3">
            Whichever you choose, your previous clock will be saved to your journey history: nothing is ever destroyed.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <button
              type="button"
              onClick={() => { setAnswers((a) => ({ ...a, q9_clock_action: 'reset' })); setConfirmReset(false); }}
              className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-colors ${
                answers.q9_clock_action === 'reset' ? 'bg-red-50 border-red-500' : 'bg-white border-zinc-200 hover:border-red-300'
              }`}
              data-testid="q9-reset-btn"
            >
              <RotateCcw className={`w-5 h-5 flex-shrink-0 ${answers.q9_clock_action === 'reset' ? 'text-red-600' : 'text-zinc-500'}`} />
              <div>
                <div className="text-sm font-bold text-zinc-900">Reset Sobriety Clock</div>
                <div className="text-[11px] text-zinc-500">Start a new clock from today. Previous clock archived.</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => { setAnswers((a) => ({ ...a, q9_clock_action: 'keep' })); setConfirmReset(false); }}
              className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-colors ${
                answers.q9_clock_action === 'keep' ? 'bg-emerald-50 border-emerald-500' : 'bg-white border-zinc-200 hover:border-emerald-300'
              }`}
              data-testid="q9-keep-btn"
            >
              <ShieldCheck className={`w-5 h-5 flex-shrink-0 ${answers.q9_clock_action === 'keep' ? 'text-emerald-600' : 'text-zinc-500'}`} />
              <div>
                <div className="text-sm font-bold text-zinc-900">Keep Current Clock</div>
                <div className="text-[11px] text-zinc-500">My clock keeps running exactly as it was.</div>
              </div>
            </button>
          </div>

          {answers.q9_clock_action === 'reset' && (
            <div className="border-t border-zinc-200 pt-3 space-y-3" data-testid="q9-reset-details">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-700 mb-1">New clean/sober date *</label>
                  <input
                    type="date"
                    value={newSobrietyDate}
                    onChange={(e) => setNewSobrietyDate(e.target.value)}
                    className="w-full border border-zinc-300 rounded p-1.5 text-sm"
                    data-testid="q9-new-date"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-700 mb-1">Time (optional)</label>
                  <input
                    type="time"
                    value={newSobrietyTime}
                    onChange={(e) => setNewSobrietyTime(e.target.value)}
                    className="w-full border border-zinc-300 rounded p-1.5 text-sm"
                    data-testid="q9-new-time"
                  />
                </div>
              </div>
              <label className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmReset}
                  onChange={(e) => setConfirmReset(e.target.checked)}
                  className="mt-0.5"
                  data-testid="q9-confirm-checkbox"
                />
                <span className="text-xs text-amber-900">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1 text-amber-700" />
                  <strong>Are you sure you want to reset your sobriety clock?</strong> Your previous clock will be saved and can still be viewed. This cannot be undone from the app.
                </span>
              </label>
            </div>
          )}
        </div>

        <button
          onClick={doSubmit}
          disabled={!canSubmit || submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl mb-8"
          data-testid="reengagement-submit"
        >
          {submitting ? 'Submitting…' : 'Submit & resume programme'}
        </button>
      </div>
    </div>
  );
}
